import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FileText, AlertTriangle, Download, Upload, Link2, ExternalLink, ShieldCheck } from 'lucide-react';
import {
  getOrderPrescription,
  saveOrderPrescriptionLink,
  uploadOrderPrescription,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import './ManageOrders.css';
import './PrescriptionUploadModal.css';

/**
 * Add (or replace) the prescription on an order tagged "Prescription" — either
 * a PDF upload or a link to it (e.g. one issued on a telemedicine platform).
 * Whichever is submitted replaces what was there before.
 *
 * Such an order cannot get a label or a manual AWB until a prescription is on
 * file. `mode="required"` is used when a label attempt was just refused, and
 * leads with that warning.
 *
 * A PDF is private on the server: it is read through a short-lived signed URL
 * fetched at click time, never through a link kept on the order.
 */

// Mirrors PRESCRIPTION_MAX_BYTES on the server, so a bad file is caught before
// the upload rather than after it.
export const PRESCRIPTION_MAX_BYTES = 5 * 1024 * 1024;

const sizeLabel = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Ticked before every submission. Mirrors PRESCRIPTION_ACKNOWLEDGEMENT on the
// server, which refuses a submission without it and records this wording in
// the order's audit log — change both together.
export const PRESCRIPTION_ACKNOWLEDGEMENT =
  'I confirm that the customer details and prescription/doctor consultation record have been ' +
  'verified and match the order, and that the correct prescription has been uploaded for dispatch.';

// Mirrors PRESCRIPTION_LINK_MAX_LENGTH on the server.
const LINK_MAX_LENGTH = 2048;

/** Why this link cannot be saved, or '' when it can. */
const linkProblem = (raw) => {
  const link = raw.trim();
  if (!link) return 'Enter a link.';
  if (link.length > LINK_MAX_LENGTH) return 'The link is too long.';
  try {
    const { protocol, hostname } = new URL(link);
    if ((protocol !== 'http:' && protocol !== 'https:') || !hostname.includes('.')) {
      return 'Enter a valid link starting with http:// or https://.';
    }
  } catch {
    return 'Enter a valid link starting with http:// or https://.';
  }
  return '';
};

/** Why this file cannot be uploaded, or '' when it can. */
const fileProblem = (file) => {
  if (!file) return 'Choose a PDF.';
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!isPdf) return 'The prescription must be a PDF.';
  if (file.size > PRESCRIPTION_MAX_BYTES) return `Over the 5 MB limit (${sizeLabel(file.size)}).`;
  return '';
};

/** Whether this order still needs a prescription before it can ship. */
export const orderNeedsPrescription = (order) =>
  Boolean(order?.requiresPrescription && !order?.prescription?.uploaded);

/** Open the order's prescription: a PDF through a fresh signed URL, or its link. */
export const openOrderPrescription = async (orderId) => {
  try {
    const data = await getOrderPrescription(orderId);
    const url = data?.prescription?.download_url || data?.prescription?.link;
    if (!url) {
      showToast({ type: 'error', message: 'No prescription has been uploaded for this order.' });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    showToast({ type: 'error', message: err?.message || 'Could not open the prescription.' });
  }
};

export default function PrescriptionUploadModal({
  order,
  mode = 'upload',
  canUpload = true,
  onClose,
  onSaved,
}) {
  const existing = order?.prescription?.uploaded ? order.prescription : null;
  const existingIsLink = existing?.kind === 'link';

  // 'file' or 'link' — the vendor picks one; opens on whatever is on file now.
  const [source, setSource] = useState(existingIsLink ? 'link' : 'file');
  const [file, setFile] = useState(null);
  const [link, setLink] = useState('');
  const [linkTouched, setLinkTouched] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const picker = useRef(null);

  const problem =
    source === 'file'
      ? (file ? fileProblem(file) : '')
      : (linkTouched || link ? linkProblem(link) : '');
  const ready = source === 'file' ? Boolean(file) : Boolean(link.trim());
  const canSave = canUpload && !saving && ready && !problem && confirmed;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const data =
        source === 'file'
          ? await uploadOrderPrescription(order.id, file, confirmed)
          : await saveOrderPrescriptionLink(order.id, link.trim(), confirmed);
      showToast({
        type: 'success',
        message: `Prescription ${existing ? 'replaced' : 'added'} for ${order.orderNumber}.`,
      });
      onSaved?.(data?.prescription || null);
      onClose();
    } catch (err) {
      setError(err?.message || 'The upload failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="modalOverlay"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div className="prescriptionModal" onClick={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <div>
            <h3>{existing ? 'Prescription' : 'Add prescription'}</h3>
            <span className="prescriptionModalSub">{order.orderNumber}</span>
          </div>
          <button
            type="button"
            className="closeModalBtn"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modalBody">
          {mode === 'required' && !existing && (
            <div className="prescriptionBanner">
              <AlertTriangle size={16} />
              <span>
                This order contains a prescription product. Upload the customer's prescription or
                add a link to it to generate the shipping label.
              </span>
            </div>
          )}

          {existing && (
            <div className="prescriptionCurrent">
              {existingIsLink ? <Link2 size={18} /> : <FileText size={18} />}
              <div className="prescriptionCurrentText">
                <span
                  className="prescriptionCurrentName"
                  title={existingIsLink ? existing.link_host : existing.original_filename}
                >
                  {existingIsLink ? `Link · ${existing.link_host || 'external'}` : existing.original_filename}
                </span>
                <span className="helpText">
                  {existingIsLink ? 'Prescription link' : sizeLabel(existing.file_size || 0)}
                  {existing.uploaded_at &&
                    ` · ${new Date(existing.uploaded_at).toLocaleString('en-IN')}`}
                  {existing.uploaded_by_name && ` · ${existing.uploaded_by_name}`}
                </span>
              </div>
              <button
                type="button"
                className="prescriptionLinkBtn"
                onClick={() => openOrderPrescription(order.id)}
              >
                {existingIsLink ? (
                  <><ExternalLink size={14} /> Open</>
                ) : (
                  <><Download size={14} /> Download</>
                )}
              </button>
            </div>
          )}

          {canUpload ? (
            <div className="formGroup">
              <label>{existing ? 'Replace with' : 'Add the prescription as'}</label>
              <div className="prescriptionSourceToggle" role="tablist" aria-label="Prescription source">
                {[
                  { key: 'file', label: 'Upload PDF', Icon: Upload },
                  { key: 'link', label: 'Add a link', Icon: Link2 },
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={source === key}
                    className={`prescriptionSourceOption ${source === key ? 'isActive' : ''}`}
                    onClick={() => {
                      setSource(key);
                      setError('');
                    }}
                    disabled={saving}
                  >
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>

              {source === 'file' ? (
                <>
                  <input
                    ref={picker}
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null);
                      setError('');
                      // Cleared so choosing the same file again still fires.
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className={`prescriptionDropzone ${problem ? 'hasError' : ''}`}
                    onClick={() => picker.current?.click()}
                    disabled={saving}
                  >
                    <Upload size={18} />
                    {file ? (
                      <span className="prescriptionDropzoneFile">
                        <span className="prescriptionCurrentName">{file.name}</span>
                        <span className="helpText">{sizeLabel(file.size)}</span>
                      </span>
                    ) : (
                      <span>Choose a PDF…</span>
                    )}
                  </button>
                  <div className="helpText">PDF only, up to 5 MB.</div>
                </>
              ) : (
                <>
                  <input
                    type="url"
                    inputMode="url"
                    className={problem ? 'error' : ''}
                    placeholder="https://"
                    value={link}
                    maxLength={LINK_MAX_LENGTH}
                    onChange={(e) => {
                      setLink(e.target.value);
                      setError('');
                    }}
                    onBlur={() => setLinkTouched(true)}
                    disabled={saving}
                    autoFocus
                  />
                  <div className="helpText">
                    A link to the prescription, e.g. from a teleconsultation platform. Make sure it
                    opens without a login.
                  </div>
                </>
              )}
              {problem && <div className="errorMessage">{problem}</div>}
            </div>
          ) : (
            existing && (
              <div className="helpText">
                A label has been generated for this order, so the prescription can no longer be
                changed here. Contact an admin if it needs replacing.
              </div>
            )
          )}

          {error && <div className="errorMessage">{error}</div>}

          {canUpload && (
            <label
              className={`prescriptionConfirm ${confirmed ? 'isChecked' : ''} ${
                ready && !problem && !confirmed ? 'needsAttention' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={saving}
                required
                aria-required="true"
              />
              <span className="prescriptionConfirmText">
                <span className="prescriptionConfirmTitle">
                  <ShieldCheck size={14} /> Verification required
                  <span className="prescriptionConfirmReq" aria-hidden="true">*</span>
                </span>
                {PRESCRIPTION_ACKNOWLEDGEMENT}
              </span>
            </label>
          )}
        </div>

        <div className="modalFooter">
          <button type="button" className="cancelBtn" onClick={onClose} disabled={saving}>
            {canUpload ? 'Cancel' : 'Close'}
          </button>
          {canUpload && (
            <button
              type="button"
              className="saveBtn"
              onClick={handleSave}
              disabled={!canSave}
              title={ready && !problem && !confirmed ? 'Tick the verification box to continue' : undefined}
            >
              {saving
                ? 'Saving…'
                : existing
                  ? 'Replace'
                  : source === 'file'
                    ? 'Upload'
                    : 'Save link'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
